import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import apiService from '../services/apiService';
import './WindDirectionCompass.css';

import { PERIOD_OPTIONS } from './PeriodSelector';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Convert m/s to knots (1 m/s = 1.94384 knots)
const msToKnots = (ms) => {
  if (ms === null || ms === undefined) return null;
  return (ms * 1.94384).toFixed(1);
};

const WindDirectionCompass = ({ selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async (period, year, month, year12, month12) => {
    try {
      setLoading(true);
      setError(null);
      
      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      let response;
      if (period === 'select') {
        response = await apiService.getWindDirection(null, year, month);
      } else if (period === '12months') {
        response = await apiService.getWindDirection(12, year12, month12);
      } else {
        response = await apiService.getWindDirection(periodConfig.months);
      }
      setData(response);
    } catch (err) {
      console.error('Failed to load wind direction data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
    const interval = setInterval(() => loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month), 60000);
    return () => clearInterval(interval);
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadData]);

  const getMaxCount = () => {
    if (!data?.distribution) return 1;
    return Math.max(...data.distribution.map(d => d.count), 1);
  };

  const getBarHeight = (count) => {
    const maxCount = getMaxCount();
    const percentage = (count / maxCount) * 100;
    return Math.max(percentage, 5);
  };

  const getHourlyChartData = () => {
    if (!data?.hourlyWindSpeed || data.hourlyWindSpeed.length === 0) return null;
    
    return {
      labels: data.hourlyWindSpeed.map(h => `${h.hour}:00`),
      datasets: [
        {
          type: 'bar',
          label: 'Avg Speed',
          data: data.hourlyWindSpeed.map(h => h.avgSpeed != null ? (h.avgSpeed * 1.94384) : null),
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1,
          borderRadius: 2,
          order: 2
        },
        {
          type: 'line',
          label: 'Max Speed',
          data: data.hourlyWindSpeed.map(h => h.maxSpeed != null ? (h.maxSpeed * 1.94384) : null),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.3,
          order: 1
        }
      ]
    };
  };

  const hourlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          usePointStyle: true,
          padding: 10,
          font: { size: 10 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: function(context) {
            return `${context.parsed.y?.toFixed(1)} kts`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: { size: 10 },
          callback: function(value) {
            return value + ' kts';
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        beginAtZero: true
      }
    }
  };

  return (
    <div className="wind-compass-card">
      <div className="compass-header">
        <h3>Wind Direction</h3>
      </div>

      {loading && !data ? (
        <div className="compass-loading">Loading wind data...</div>
      ) : error ? (
        <div className="compass-error">{error}</div>
      ) : (
        <div className="compass-content">
          <div className="compass-container">
            <div className="compass-rose">
              <div className="compass-background">
                <div className="compass-circle outer"></div>
                <div className="compass-circle middle"></div>
                <div className="compass-circle inner"></div>
              </div>
              
              <div className="compass-labels">
                <span className="compass-label n">0°</span>
                <span className="compass-label ne">45°</span>
                <span className="compass-label e">90°</span>
                <span className="compass-label se">135°</span>
                <span className="compass-label s">180°</span>
                <span className="compass-label sw">225°</span>
                <span className="compass-label w">270°</span>
                <span className="compass-label nw">315°</span>
              </div>

              {data?.distribution && (
                <div className="compass-bars">
                  {data.distribution.map((d) => {
                    const height = getBarHeight(d.count);
                    const periodConfig = PERIOD_OPTIONS.find(p => p.id === selectedPeriod);
                    return (
                      <div 
                        key={d.bin} 
                        className="compass-bar-container"
                        style={{ transform: `rotate(${d.degrees}deg)` }}
                      >
                        <div 
                          className="compass-bar"
                          style={{ height: `${height}%`, backgroundColor: periodConfig?.barColor }}
                          title={`${d.degrees}°-${d.degrees + 9}°: ${d.count.toLocaleString()} readings, avg ${msToKnots(d.avgSpeed)} kts`}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="compass-center">
                <div className="center-label">Wind</div>
                <div className="center-label">Rose</div>
              </div>
            </div>
          </div>

          <div className="hourly-wind-chart">
            <h4>Wind Speed by Hour</h4>
            <div className="chart-container">
              {data?.hourlyWindSpeed && data.hourlyWindSpeed.length > 0 && (
                <Bar data={getHourlyChartData()} options={hourlyChartOptions} />
              )}
            </div>
          </div>

          <div className="distribution-legend">
            <div className="legend-title">Top Wind Directions</div>
            <div className="legend-items">
              {data?.distribution
                ?.filter(d => d.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 8)
                .map(d => (
                <div key={d.bin} className="legend-item">
                  <span className="legend-dir">{d.degrees}°</span>
                  <div className="legend-bar-container">
                    <div 
                      className="legend-bar"
                      style={{ width: `${getBarHeight(d.count)}%` }}
                    ></div>
                  </div>
                  <span className="legend-count">{d.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WindDirectionCompass;
